import { useState } from "react";
import { IDriverActiveOrder } from "../../models/IDriver";
import { IOrderDetail } from "../../models/IOrderDetail";
import { getOrderDetailById } from "../../services/order";
import { MapTarget } from "./DriverMapModal";
import ReportIncidentModal from "../../incident-module/components/ReportIncidentModal";
import {
  DELIVERY_STATUS_META,
  collectsAtDoor,
  customerAddressText,
  formatMoney,
  formatRelative,
  formatTime,
  hasCoords,
  mapsSearchUrl,
  normalizeDeliveryStatus,
  telHref,
} from "../utils";

interface Props {
  order: IDriverActiveOrder;
  now: number;
  busy: boolean;
  onPickedUp: () => void;
  onDeliver: () => void;
  onCancel: () => void;
  onOpenMap: (target: MapTarget) => void;
}

interface StopProps {
  step: number;
  label: string;
  active: boolean;
  name: string;
  address: string;
  note?: string | null;
  phone?: string | null;
  mapTarget: MapTarget | null;
  onOpenMap: (target: MapTarget) => void;
}

function Stop({ step, label, active, name, address, note, phone, mapTarget, onOpenMap }: StopProps) {
  const canNavigate = !!mapTarget || !!address;
  const actionCount = (phone ? 1 : 0) + (canNavigate ? 1 : 0);
  return (
    <div className={`dp-stop${active ? " is-active" : ""}`}>
      <div className="dp-stop__label">
        {step} · {label}
        {active && <span className="dp-stop__now">ŞİMDİ</span>}
      </div>
      <div className="dp-stop__name">{name}</div>
      {address && <div className="dp-stop__addr">{address}</div>}
      {note && (
        <div className="dp-stop__note">
          <i className="bi bi-info-circle-fill me-1"></i>{note}
        </div>
      )}
      {actionCount > 0 && (
        <div className={`dp-stop__actions${actionCount === 1 ? " dp-stop__actions--single" : ""}`}>
          {phone && (
            <a className="dp-btn dp-btn--ghost" href={telHref(phone)}>
              <i className="bi bi-telephone-fill"></i> Ara
            </a>
          )}
          {mapTarget ? (
            <button className="dp-btn dp-btn--nav" onClick={() => onOpenMap(mapTarget)}>
              <i className="bi bi-signpost-split-fill"></i> Yol tarifi
            </button>
          ) : address ? (
            <a className="dp-btn dp-btn--nav" href={mapsSearchUrl(address)} target="_blank" rel="noopener noreferrer">
              <i className="bi bi-map-fill"></i> Haritada ara
            </a>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function ActiveOrderCard({ order, now, busy, onPickedUp, onDeliver, onCancel, onOpenMap }: Props) {
  const status = normalizeDeliveryStatus(order.deliveryStatus);
  const meta = DELIVERY_STATUS_META[status];
  const isPickedUp = status === "PickedUp";
  const collect = collectsAtDoor(order);

  const [itemsOpen, setItemsOpen] = useState(false);
  const [detail, setDetail] = useState<IOrderDetail | null>(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const toggleItems = () => {
    const next = !itemsOpen;
    setItemsOpen(next);
    if (next && !detail && !itemsLoading) {
      setItemsLoading(true);
      setItemsError(false);
      getOrderDetailById(order.orderId)
        .then((res) => setDetail(res.data))
        .catch(() => setItemsError(true))
        .finally(() => setItemsLoading(false));
    }
  };

  const r = order.restaurant;
  const restaurantTarget: MapTarget | null = hasCoords(r.latitude, r.longitude)
    ? { kind: "restaurant", name: r.name ?? "Restoran", address: r.address ?? "", lat: r.latitude!, lng: r.longitude!, phone: r.phoneNo }
    : null;

  const a = order.deliveryAddress;
  const customerName = order.customer?.name || "Misafir müşteri";
  const customerAddress = customerAddressText(order);
  const customerTarget: MapTarget | null = a && hasCoords(a.latitude, a.longitude)
    ? { kind: "customer", name: customerName, address: customerAddress, lat: a.latitude!, lng: a.longitude!, phone: order.customer?.telNo }
    : null;

  return (
    <div className="dp-card">
      <div className="dp-card__head">
        <div>
          <div className="dp-card__no">#{order.orderNameUniqe ?? order.orderId.slice(0, 8)}</div>
          <div className="dp-card__time">
            <i className="bi bi-clock me-1"></i>
            {formatTime(order.assignedAt)} · {formatRelative(order.assignedAt, now)} atandı
          </div>
        </div>
        <span className="dp-chip" style={{ background: meta.bg, color: meta.color }}>
          <i className={`bi ${meta.icon}`}></i>{meta.label}
        </span>
      </div>

      <div className="dp-steps">
        <div className={`dp-step ${isPickedUp ? "is-done" : "is-current"}`}>
          <div className="dp-step__dot"><i className={`bi ${isPickedUp ? "bi-check-lg" : "bi-shop"}`}></i></div>
          Restorandan al
        </div>
        <div className={`dp-step__line${isPickedUp ? " is-done" : ""}`} />
        <div className={`dp-step ${isPickedUp ? "is-current" : ""}`}>
          <div className="dp-step__dot"><i className="bi bi-bicycle"></i></div>
          Yola çık
        </div>
        <div className="dp-step__line" />
        <div className="dp-step">
          <div className="dp-step__dot"><i className="bi bi-house-door-fill"></i></div>
          Teslim et
        </div>
      </div>

      <Stop
        step={1}
        label="Alış noktası"
        active={!isPickedUp}
        name={r.name ?? "Restoran"}
        address={r.address ?? ""}
        phone={r.phoneNo}
        mapTarget={restaurantTarget}
        onOpenMap={onOpenMap}
      />
      <Stop
        step={2}
        label="Teslimat noktası"
        active={isPickedUp}
        name={customerName}
        address={customerAddress}
        note={a?.note}
        phone={order.customer?.telNo}
        mapTarget={customerTarget}
        onOpenMap={onOpenMap}
      />

      <div className={`dp-pay ${collect ? "dp-pay--collect" : "dp-pay--plain"}`}>
        <div>
          <div className="dp-pay__label">
            <i className={`bi ${collect ? "bi-cash-coin" : "bi-wallet2"} me-1`}></i>
            {collect ? "Kapıda tahsil edilecek" : "Ödeme"}
          </div>
          {order.paymentMethod?.nameTr && <div className="dp-pay__method">{order.paymentMethod.nameTr}</div>}
        </div>
        <div className="dp-pay__amount">{formatMoney(order.orderPrice, order.currencyDisplayText)}</div>
      </div>

      {order.orderNote && (
        <div className="dp-note">
          <i className="bi bi-chat-square-text me-1"></i><strong>Sipariş notu:</strong> {order.orderNote}
        </div>
      )}

      <button className="dp-items-toggle" onClick={toggleItems} aria-expanded={itemsOpen}>
        <span><i className="bi bi-bag me-1"></i>Sipariş içeriği</span>
        <i className={`bi ${itemsOpen ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
      </button>
      {itemsOpen && (
        <div className="dp-items">
          {itemsLoading ? (
            <div style={{ textAlign: "center", padding: 6 }}>
              <span className="spinner-border spinner-border-sm" style={{ color: "#ea580c" }} role="status" />
            </div>
          ) : itemsError ? (
            <span style={{ color: "#b91c1c" }}>Sipariş içeriği yüklenemedi.</span>
          ) : detail && detail.foodPreOrder.length > 0 ? (
            detail.foodPreOrder.map((item) => (
              <div key={item.id} className="dp-items__row">
                <div>
                  <strong>{item.foodQuantity}×</strong> {item.food?.foodNameTr}
                  {item.foodOrderNote && <div className="dp-items__sub">{item.foodOrderNote}</div>}
                </div>
              </div>
            ))
          ) : (
            <span style={{ color: "#9ca3af" }}>Ürün bilgisi yok.</span>
          )}
        </div>
      )}

      <div className="dp-card__actions">
        {isPickedUp ? (
          <button className="dp-btn dp-btn--success dp-btn--lg" onClick={onDeliver} disabled={busy}>
            {busy ? <span className="spinner-border spinner-border-sm" role="status" /> : <><i className="bi bi-check2-circle"></i> Müşteriye teslim ettim</>}
          </button>
        ) : (
          <button className="dp-btn dp-btn--primary dp-btn--lg" onClick={onPickedUp} disabled={busy}>
            {busy ? <span className="spinner-border spinner-border-sm" role="status" /> : <><i className="bi bi-bag-check-fill"></i> Siparişi teslim aldım</>}
          </button>
        )}
        <div style={{ display: "flex", justifyContent: "center", gap: 18, flexWrap: "wrap" }}>
          <button className="dp-link-btn" onClick={() => setReportOpen(true)} disabled={busy} style={{ color: "#dc2626" }}>
            <i className="bi bi-flag me-1"></i>Sorun bildir
          </button>
          <button className="dp-link-btn" onClick={onCancel} disabled={busy}>Teslimatı iptal et</button>
        </div>
      </div>

      {reportOpen && (
        <ReportIncidentModal
          mode="courier"
          orderId={order.orderId}
          orderCode={order.orderNameUniqe ?? undefined}
          customerName={order.customer?.name ?? undefined}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
}
